import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useContexts";
import {
  getSession,
  setPersistent,
  setSession,
} from "../../utils/tokenStorage";

const PersistLogin = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { auth, setAuth } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const getTokenFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  };

  // ✅ Fetch full user details using the token, then login
  const handleTokenLogin = async (token: string) => {
    try {
      const res = await fetch(
        // ← replace with your endpoint
        `${import.meta.env.VITE_BACKEND_BASE_URL}/api/v1/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const json = await res.json();

      // Adjust this path based on your actual API response shape
      const user = json?.data?.user ?? json?.data ?? {};

      // "Remember me" defaults to on (Form.tsx: `defaultChecked` + `useState(true)`), so
      // an entry path that offers no such choice persists as well. This restores the
      // pre-MED-683 behaviour and is product-signed-off.
      setPersistent(true);
      setSession({ token, user });

      if (user?.company?.pcn_registration_status) {
        sessionStorage.setItem(
          "regStatus",
          user.company.pcn_registration_status,
        );
      }

      // ── Hydrate auth context ──
      setAuth({ token, user });
    } catch (err) {
      console.error("Token login failed:", err);
      // Token was invalid — fall through to stored session or unauthenticated
    } finally {
      // ── Strip ?token from URL regardless of success/failure ──
      navigate(location.pathname, { replace: true });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        // URL token takes highest priority ──
        const tokenFromUrl = getTokenFromUrl();
        if (tokenFromUrl) {
          await handleTokenLogin(tokenFromUrl);
          return;
        }

        // Fallback to whichever store the "Remember me" choice selected ──
        const session = getSession();
        if (session) setAuth(session);
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    if (!auth?.token) {
      initAuth();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  // Only the ?token= handoff is asynchronous, so it is the only case worth holding the
  // route for. Storage hydration is synchronous in AuthProvider, so gating on `persist`
  // here — as this previously did — only ever caused a redirect to /login before
  // hydration had run (MED-683).
  const hasUrlToken = Boolean(getTokenFromUrl());

  return <>{hasUrlToken && isLoading ? <p>Loading page...</p> : <Outlet />}</>;
};

export default PersistLogin;
